import seoRedirects from "./data/seoRedirects.json";
import "@/App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import { RETIRED_ROUTES, RETIRED_PREFIXES } from "@/data/retiredRoutes";

// Eager: the homepage is the funnel's entry point and the most common landing
// surface, so it stays in the main bundle rather than costing an extra chunk
// fetch on the LCP.
import HomePage from "@/pages/HomePage";
import { STANDALONE_OFFERS, applicationHref } from "./data/applicationProducts";
import AnalyticsTracker from "@/lib/AnalyticsTracker";
import ScrollToTop from "@/lib/ScrollToTop";
import CookieConsent from "@/components/landing/CookieConsent";
import MailingListPopup from "@/components/landing/MailingListPopup";
import SitePromos from "@/components/landing/SitePromos";

// Lazy: every other route splits into its own chunk. Tiny "loading" UI fills
// the brief gap before the route chunk arrives (typically <100ms on modern
// networks because chunks are tiny and prefetched after the main bundle).

// --- The funnel -----------------------------------------------------------
const GrowthPointsPage        = lazy(() => import("@/pages/GrowthPointsPage"));
const PointCatalogPage        = lazy(() => import("@/pages/PointCatalogPage"));
const GrowthApplicationPage   = lazy(() => import("@/pages/GrowthApplicationPage"));
const ApplicationOutcomePage  = lazy(() => import("@/pages/ApplicationOutcomePage"));
const ApplicationBookedPage   = lazy(() => import("@/pages/ApplicationBookedPage"));
const ResultsPage             = lazy(() => import("@/pages/ResultsPage"));
const ResourcesPage           = lazy(() => import("@/pages/ResourcesPage"));
const BookingDatesPage        = lazy(() => import("@/pages/BookingDatesPage"));

// --- Free tools, kept because the funnel's Tier C routing sends people here -
const DigitalStemCellLandingPage = lazy(() => import("@/pages/products/DigitalStemCellLandingPage"));
const SeoReportPage              = lazy(() => import("@/pages/SeoReportPage"));
const MarketingAuditFreePage     = lazy(() => import("@/pages/MarketingAuditFreePage"));
const PossibilityLatticePage     = lazy(() => import("@/pages/PossibilityLatticePage"));
const PossibilityLatticeViewPage = lazy(() => import("@/pages/PossibilityLatticeViewPage"));
const PredictionEnginePage       = lazy(() => import("@/pages/PredictionEnginePage"));
const PredictionEngineViewPage   = lazy(() => import("@/pages/PredictionEngineViewPage"));
const RevenueMasterclassPage     = lazy(() => import("@/pages/RevenueMasterclassPage"));

// --- Content --------------------------------------------------------------
const AboutBowTieKreativePage = lazy(() => import("@/pages/AboutBowTieKreativePage"));
const BlogIndexPage           = lazy(() => import("@/pages/BlogIndexPage"));
const BlogPostPage            = lazy(() => import("@/pages/BlogPostPage"));
const DocsIndexPage           = lazy(() => import("@/pages/DocsIndexPage"));
const DocsArticlePage         = lazy(() => import("@/pages/DocsArticlePage"));
const LocationPage            = lazy(() => import("@/pages/seo/LocationPage"));
const IndustryPage            = lazy(() => import("@/pages/seo/IndustryPage"));
const LocationsIndexPage      = lazy(() => import("@/pages/seo/SeoIndexPage").then(m => ({ default: m.LocationsIndexPage })));
const IndustriesIndexPage     = lazy(() => import("@/pages/seo/SeoIndexPage").then(m => ({ default: m.IndustriesIndexPage })));

const DynamicPage             = lazy(() => import("@/pages/DynamicPage"));

// --- Legal + account ------------------------------------------------------
const TermsAndConditionsPage = lazy(() => import("@/pages/legal/TermsAndConditionsPage"));
const RefundPolicyPage       = lazy(() => import("@/pages/legal/RefundPolicyPage"));
const PrivacyPolicyPage      = lazy(() => import("@/pages/legal/PrivacyPolicyPage"));
const CookiePolicyPage       = lazy(() => import("@/pages/legal/CookiePolicyPage"));
const SupportPage            = lazy(() => import("@/pages/SupportFormPage"));
const PublicFormPage         = lazy(() => import("@/pages/PublicFormPage"));
const EmailUnsubscribePage   = lazy(() => import("@/pages/EmailUnsubscribePage"));
const CancelSubscriptionPage = lazy(() => import("@/pages/CancelSubscriptionPage"));

// --- Admin ----------------------------------------------------------------
const AdminLoginPage             = lazy(() => import("@/pages/AdminLoginPage"));
const AdminCMSHubPage            = lazy(() => import("@/pages/AdminCMSHubPage"));
const AdminResendPage            = lazy(() => import("@/pages/AdminResendPage"));
const AdminBookingsPage          = lazy(() => import("@/pages/AdminBookingsPage"));
const AdminOutreachPage          = lazy(() => import("@/pages/AdminOutreachPage"));
const AdminSyncStatusPage        = lazy(() => import("@/pages/AdminSyncStatusPage"));
const AdminEmailDripsPage        = lazy(() => import("@/pages/AdminEmailDripsPage"));
const AdminApiTokensPage         = lazy(() => import("@/pages/AdminApiTokensPage"));
const AdminPagesPage             = lazy(() => import("@/pages/AdminPagesPage"));
const AdminMenusPage             = lazy(() => import("@/pages/AdminMenusPage"));
const AdminMenuItemsPage         = lazy(() => import("@/pages/AdminMenuItemsPage"));
const AdminBlogPage              = lazy(() => import("@/pages/AdminBlogPage"));
const AdminWorkflowsPage         = lazy(() => import("@/pages/AdminWorkflowsPage"));
const AdminTestimonialsPage      = lazy(() => import("@/pages/AdminTestimonialsPage"));
const AdminPortfolioPage         = lazy(() => import("@/pages/AdminPortfolioPage"));
const AdminMarketingBlocksPage   = lazy(() => import("@/pages/AdminMarketingBlocksPage"));
const AdminSitePromosPage        = lazy(() => import("@/pages/AdminSitePromosPage"));
const AdminPushNotificationsPage = lazy(() => import("@/pages/AdminPushNotificationsPage"));
const AdminSuitedashMappingsPage = lazy(() => import("@/pages/AdminSuitedashMappingsPage"));
const AdminSitePagesIndexPage    = lazy(() => import("@/pages/AdminSitePagesIndexPage"));
const AdminSeoIndustriesPage     = lazy(() => import("@/pages/AdminSeoIndustriesPage"));
const AdminSeoLocationsPage      = lazy(() => import("@/pages/AdminSeoLocationsPage"));
const AdminFormsPage             = lazy(() => import("@/pages/AdminFormsPage"));
const AdminFormSubmissionsPage   = lazy(() => import("@/pages/AdminFormSubmissionsPage"));
const AdminApplicationsPage      = lazy(() => import("@/pages/AdminApplicationsPage"));
const DataDiagramsPage           = lazy(() => import("@/pages/DataDiagramsPage"));

// Minimal fallback while a route chunk loads. Matches the dark brand BG so
// there's no white flash. Avoids spinner clutter — bias toward "loaded fast"
// over "look busy".
const RouteFallback = () => (
  <div data-testid="route-fallback" className="min-h-screen bg-ink-900" aria-busy="true" />
);

const OffersPage = lazy(() => import("@/pages/OffersPage"));

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsTracker />
          <CookieConsent />
          <MailingListPopup />
          {/* DB-driven banners + modals + toasts. Reads
              /api/site-promos/active?page=<route> on every navigation and
              renders the matching pre-styled component(s). Operator adds
              promos through /admin/site-promos. */}
          <SitePromos />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* ---------------------------------------------------------
                  THE FUNNEL

                  Home → GrowthPoints → Application → routed outcome → call.
                  Everything else on the site either feeds this path or
                  supports it. Nothing else sells.
                  --------------------------------------------------------- */}
              <Route path="/" element={<HomePage />} />
              <Route path="/growth-points" element={<GrowthPointsPage />} />
              <Route path="/growth-points/catalog" element={<PointCatalogPage />} />
              <Route path="/apply" element={<GrowthApplicationPage />} />
              <Route path="/products" element={<OffersPage />} />
              <Route path="/marketplace" element={<Navigate to="/products" replace />} />
              {STANDALONE_OFFERS.map(offer => <Route key={offer.id} path={offer.legacyPath} element={<Navigate to={applicationHref(offer.id)} replace />} />)}
              {/* The outcome page does NOT carry the tier in its URL. The tier
                  decides whether the booking calendar is exposed, and it is
                  scored server-side — putting it in the path would let anyone
                  promote themselves to Tier A by editing the address bar. */}
              <Route path="/apply/status/:token" element={<ApplicationOutcomePage />} />
              <Route path="/apply/booked/:token" element={<ApplicationBookedPage />} />
              <Route path="/results" element={<ResultsPage />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/booking/:slug" element={<BookingDatesPage />} />

              {/* ---------------------------------------------------------
                  FREE TOOLS — the Tier C routing sends unqualified applicants
                  to these, so they are load-bearing for the funnel.
                  --------------------------------------------------------- */}
              <Route path="/seo-report"           element={<DigitalStemCellLandingPage slug="seo-report" />} />
              <Route path="/marketing-audit-free" element={<DigitalStemCellLandingPage slug="marketing-audit-free" />} />
              <Route path="/possibility-lattice"  element={<DigitalStemCellLandingPage slug="possibility-lattice" />} />
              <Route path="/prediction-engine"    element={<DigitalStemCellLandingPage slug="prediction-engine" />} />
              <Route path="/revenue-masterclass"  element={<RevenueMasterclassPage />} />

              <Route path="/utilities/seo-report"               element={<SeoReportPage />} />
              <Route path="/utilities/marketing-audit"          element={<MarketingAuditFreePage />} />
              <Route path="/utilities/strategy-engine"          element={<PossibilityLatticePage />} />
              <Route path="/utilities/strategy-engine/v/:id"    element={<PossibilityLatticeViewPage />} />
              <Route path="/utilities/prediction-engine"        element={<PredictionEnginePage />} />
              <Route path="/utilities/prediction-engine/v/:id"  element={<PredictionEngineViewPage />} />

              {/* Legacy share URLs — keep working so existing share links don't 404 */}
              <Route path="/possibility-lattice/v/:id" element={<PossibilityLatticeViewPage />} />
              <Route path="/prediction-engine/v/:id"   element={<PredictionEngineViewPage />} />

              {/* ---------------------------------------------------------
                  CONTENT
                  --------------------------------------------------------- */}
              <Route path="/about-bow-tie-kreative" element={<AboutBowTieKreativePage />} />
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/docs" element={<DocsIndexPage />} />
              <Route path="/docs/:slug" element={<DocsArticlePage />} />
              <Route path="/locations" element={<LocationsIndexPage />} />
              <Route path="/locations/:slug" element={<LocationPage />} />
              <Route path="/industries" element={<IndustriesIndexPage />} />
              <Route path="/industries/:slug" element={<IndustryPage />} />
              <Route path="/case-studies" element={<Navigate to="/results" replace />} />

              {/* ---------------------------------------------------------
                  LEGAL + ACCOUNT
                  --------------------------------------------------------- */}
              <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/cookie-policy" element={<CookiePolicyPage />} />
              <Route path="/support" element={<SupportPage formKey="general" />} />
              <Route path="/support/:slug" element={<SupportPage />} />
              <Route path="/forms/:slug" element={<PublicFormPage />} />
              <Route path="/email/unsubscribe" element={<EmailUnsubscribePage />} />
              <Route path="/cancel-subscription" element={<CancelSubscriptionPage />} />

              {/* ---------------------------------------------------------
                  ADMIN
                  --------------------------------------------------------- */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin/cms" element={<AdminCMSHubPage />} />
              <Route path="/admin/applications" element={<AdminApplicationsPage />} />
              <Route path="/admin/resend" element={<AdminResendPage />} />
              <Route path="/admin/bookings" element={<AdminBookingsPage />} />
              <Route path="/admin/outreach" element={<AdminOutreachPage />} />
              <Route path="/admin/sync-status" element={<AdminSyncStatusPage />} />
              <Route path="/admin/email-drips" element={<AdminEmailDripsPage />} />
              <Route path="/admin/api-tokens" element={<AdminApiTokensPage />} />
              <Route path="/admin/pages" element={<AdminPagesPage />} />
              <Route path="/admin/menus" element={<AdminMenusPage />} />
              <Route path="/admin/menu-items" element={<AdminMenuItemsPage />} />
              <Route path="/admin/blog" element={<AdminBlogPage />} />
              <Route path="/admin/workflows" element={<AdminWorkflowsPage />} />
              <Route path="/admin/testimonials" element={<AdminTestimonialsPage />} />
              <Route path="/admin/portfolio" element={<AdminPortfolioPage />} />
              <Route path="/admin/marketing-blocks" element={<AdminMarketingBlocksPage />} />
              <Route path="/admin/site-promos" element={<AdminSitePromosPage />} />
              <Route path="/admin/push-notifications" element={<AdminPushNotificationsPage />} />
              <Route path="/admin/suitedash-mappings" element={<AdminSuitedashMappingsPage />} />
              <Route path="/admin/site-pages" element={<AdminSitePagesIndexPage />} />
              <Route path="/admin/seo-industries" element={<AdminSeoIndustriesPage />} />
              <Route path="/admin/seo-locations" element={<AdminSeoLocationsPage />} />
              <Route path="/admin/forms" element={<AdminFormsPage />} />
              <Route path="/admin/forms/:slug/submissions" element={<AdminFormSubmissionsPage />} />
              <Route path="/admin/diagrams" element={<DataDiagramsPage />} />

              {/* ---------------------------------------------------------
                  RETIRED — the commerce estate.

                  Generated from data/retiredRoutes.js so this list and the
                  edge 301s cannot drift apart. These are client-side
                  redirects; the real 301s live in front of the app.
                  --------------------------------------------------------- */}
              {Object.entries(RETIRED_ROUTES).filter(([from]) => from !== "/marketplace" && !STANDALONE_OFFERS.some(p => p.legacyPath === from)).map(([from, to]) => (
                <Route key={from} path={from} element={<Navigate to={to} replace />} />
              ))}
              {Object.entries(RETIRED_PREFIXES).map(([from, to]) => (
                <Route key={`${from}/*`} path={`${from}/*`} element={<Navigate to={to} replace />} />
              ))}
              {/* Catches the retired GrowthPoints tier landings. React Router
                  ranks by specificity rather than declaration order, so the
                  static /growth-points/catalog above still wins over this
                  dynamic segment. */}
              <Route path="/growth-points/:tier" element={<Navigate to="/growth-points" replace />} />
              <Route path="/workshop/:slug/*" element={<Navigate to={applicationHref("workshop")} replace />} />

              {Object.entries(seoRedirects).filter(([from]) => from.startsWith("/catalog/") || from === "/about" || from === "/seo-course-2026").map(([from, to]) => <Route key={from} path={from} element={<Navigate to={to} replace />} />)}

              {/* CMS catch-all — DynamicPage hits /api/cms/pages/by-slug/<slug>
                  and renders the matching template, or NotFoundPage if no row. */}
              <Route path="*" element={<DynamicPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
